/**
 * File Upload Service
 * Handles file upload for Book Import and Chapter Replace functionality
 */

export const fileUploadService = {
    /**
     * Upload file for import/replace
     * @param file - File to upload
     * @param entityType - 'book' | 'chapter'
     * @param entityId - Entity ID
     * @param onProgress - Progress callback (0-100)
     * @returns Promise<string> - Job ID
     */
    async uploadFile(
        file: File,
        entityType: 'book' | 'chapter',
        entityId: number,
        onProgress?: (progress: number) => void
    ): Promise<string> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('entity_type', entityType);
        formData.append('entity_id', entityId.toString());

        try {
            const response = await window.$http.post(
                '/api/file-import/upload',
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                    onUploadProgress: (event) => {
                        if (onProgress && event.total) {
                            const progress = Math.round((event.loaded / event.total) * 100);
                            onProgress(progress);
                        }
                    },
                    timeout: 300000, // 5 minutes
                }
            );

            if (!response.data.success) {
                throw new Error(response.data.error || 'Upload failed');
            }

            return response.data.jobId;

        } catch (error) {
            console.error('File upload error:', error);

            // Handle different error types
            if (error.response) {
                // Server responded with error status
                const status = error.response.status;
                const data = error.response.data;

                if (status === 413) {
                    throw new Error('File too large (max 50MB)');
                } else if (status === 422) {
                    // Validation error
                    const errors = data.errors || {};
                    const firstError = Object.values(errors)[0];
                    throw new Error(Array.isArray(firstError) ? firstError[0] : firstError);
                } else if (data.error) {
                    throw new Error(data.error);
                } else {
                    throw new Error(`Server error: ${status}`);
                }
            } else if (error.code === 'ECONNABORTED') {
                throw new Error('Upload timeout. Please try again.');
            } else if (error.message) {
                throw new Error(error.message);
            } else {
                throw new Error('Failed to upload file');
            }
        }
    },

    /**
     * Check processing status
     * @param jobId - Job ID to check
     * @returns Promise<StatusResponse>
     */
    async checkStatus(jobId: string): Promise<StatusResponse> {
        try {
            const response = await window.$http.get(`/api/file-import/status/${jobId}`, {
                timeout: 10000, // 10 seconds
            });

            if (!response.data.success) {
                throw new Error(response.data.error || 'Failed to check status');
            }

            return {
                status: response.data.status,
                progress: response.data.progress || 0,
                message: response.data.message || '',
                error: response.data.error || null,
                result: response.data.result || null
            };

        } catch (error) {
            console.error('Status check error:', error);

            if (error.response) {
                const status = error.response.status;
                const data = error.response.data;

                if (status === 404) {
                    throw new Error('Job not found');
                } else if (data.error) {
                    throw new Error(data.error);
                } else {
                    throw new Error(`Server error: ${status}`);
                }
            } else if (error.code === 'ECONNABORTED') {
                throw new Error('Request timeout');
            } else if (error.message) {
                throw new Error(error.message);
            } else {
                throw new Error('Failed to check status');
            }
        }
    },

    /**
     * Cancel processing job
     * @param jobId - Job ID to cancel
     * @returns Promise<boolean> - Success status
     */
    async cancelJob(jobId: string): Promise<boolean> {
        try {
            const response = await window.$http.delete(`/api/file-import/job/${jobId}`, {
                timeout: 10000
            });

            return response.data.success || false;

        } catch (error) {
            console.error('Cancel job error:', error);
            return false;
        }
    },

    /**
     * Get supported file types
     * @returns Promise<string[]> - Array of supported MIME types
     */
    async getSupportedFileTypes(): Promise<string[]> {
        try {
            const response = await window.$http.get('/api/file-import/supported-types', {
                timeout: 10000
            });

            return response.data.types || [];

        } catch (error) {
            console.error('Get supported types error:', error);
            // Return default types
            return [
                'application/pdf',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                'application/msword',
            ];
        }
    },

    /**
     * Get upload limits
     * @returns Promise<UploadLimits>
     */
    async getUploadLimits(): Promise<UploadLimits> {
        try {
            const response = await window.$http.get('/api/file-import/limits', {
                timeout: 10000
            });

            return {
                maxFileSize: response.data.maxFileSize || 50 * 1024 * 1024,
                maxFilesPerDay: response.data.maxFilesPerDay || 10,
                supportedTypes: response.data.supportedTypes || []
            };

        } catch (error) {
            console.error('Get upload limits error:', error);
            // Return default limits
            return {
                maxFileSize: 50 * 1024 * 1024, // 50MB
                maxFilesPerDay: 10,
                supportedTypes: [
                    'application/pdf',
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                    'application/msword',
                ]
            };
        }
    }
};

// Type definitions
interface StatusResponse {
    status: 'pending' | 'processing' | 'completed' | 'failed';
    progress: number;
    message: string;
    error: string | null;
    result: any | null;
}

interface UploadLimits {
    maxFileSize: number;
    maxFilesPerDay: number;
    supportedTypes: string[];
}

// Utility functions
export const fileUploadUtils = {
    /**
     * Format file size to human readable format
     */
    formatFileSize(bytes: number): string {
        if (bytes === 0) return '0 Bytes';

        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));

        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    /**
     * Check if file type is supported
     */
    isFileTypeSupported(file: File, supportedTypes: string[]): boolean {
        const fileExtension = '.' + file.name.split('.').pop().toLowerCase();

        // Check MIME type
        if (supportedTypes.includes(file.type)) {
            return true;
        }

        // Check extension
        const extensionMap: Record<string, string[]> = {
            'application/pdf': ['.pdf'],
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
            'application/msword': ['.doc'],
        };

        for (const [mimeType, extensions] of Object.entries(extensionMap)) {
            if (supportedTypes.includes(mimeType) && extensions.includes(fileExtension)) {
                return true;
            }
        }

        return false;
    },

    /**
     * Get file icon based on file type
     */
    getFileIcon(file: File): string {
        const name = file.name.toLowerCase();

        if (name.endsWith('.pdf')) return '📄';
        if (name.endsWith('.docx') || name.endsWith('.doc')) return '📝';

        return '📎';
    },

    /**
     * Validate file before upload
     */
    validateFile(file: File, limits: UploadLimits): ValidationResult {
        // Check file size
        if (file.size > limits.maxFileSize) {
            return {
                valid: false,
                error: `File too large (max ${fileUploadUtils.formatFileSize(limits.maxFileSize)})`
            };
        }

        // Check file type
        if (!fileUploadUtils.isFileTypeSupported(file, limits.supportedTypes)) {
            return {
                valid: false,
                error: 'File type not supported'
            };
        }

        return { valid: true };
    }
};

interface ValidationResult {
    valid: boolean;
    error?: string;
}